const chapter = {
  id: 'hydrogen',
  slug: 'hydrogen',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Hydrogen',
        hindi: 'हाइड्रोजन',
  },
  estReadMinutes: 45,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '1 question in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 1 प्रश्न',
  },
  syllabusTags: ['Inorganic Chemistry', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'position',
      title: {
        english: 'Position in the Periodic Table',
        hindi: 'आवर्त सारणी में स्थिति',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Hydrogen ($1s^1$) shows similarities both with **alkali metals** (one valence electron, +1 oxidation state, forms unipositive ion) and with **halogens** (one electron short of noble-gas configuration, forms diatomic H₂, can take −1 state in hydrides). Its unique properties (smallest size, highest IE among s-block) merit a separate position; usually placed in Group 1.',
        hindi: 'हाइड्रोजन ($1s^1$) **क्षार धातुओं** (एक वैलेंस इलेक्ट्रॉन, +1 ऑक्सीकरण अवस्था, एक धनात्मक आयन बनाता है) और **हैलोजन** (नोबल-गैस विन्यास से एक इलेक्ट्रॉन कम, डायटोमिक H₂ बनाता है, हाइड्राइड्स में −1 अवस्था ले सकता है) दोनों के साथ समानता दिखाता है। इसके अद्वितीय गुण (सबसे छोटा आकार, एस-ब्लॉक के बीच उच्चतम IE) एक अलग स्थान के योग्य हैं; आमतौर पर समूह 1 में रखा जाता है। तीन समस्थानिक: **प्रोटियम** $1s^1$ (99.985 %), **ड्यूटेरियम** या D (0.015 %), **ट्रिटियम** या T (रेडियोधर्मी, उत्सर्जक, वर्ष)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Three isotopes: **protium** $_1^1H$ (99.985 %), **deuterium** $_1^2H$ or D (0.015 %), **tritium** $_1^3H$ or T (radioactive, $\\beta^-$ emitter, $t_{1/2} \\approx 12.3$ years).',
        hindi: 'तीन समस्थानिक: **प्रोटियम** $_1^1H$ (99.985 %), **ड्यूटेरियम** $_1^2H$ या D (0.015 %), **ट्रिटियम** $_1^3H$ या T (रेडियोधर्मी, $\\beta^-$ उत्सर्जक, $t_{1/2} \\approx 12.3$ वर्ष)।',
            },
            {
              english: 'Differ in mass; chemical properties similar but reaction rates and bond strengths differ (kinetic isotope effect).',
        hindi: 'द्रव्यमान में अंतर; रासायनिक गुण समान होते हैं लेकिन प्रतिक्रिया दर और बंधन शक्तियाँ भिन्न होती हैं (गतिज आइसोटोप प्रभाव)।',
            },
            {
              english: 'Most abundant element in the **universe** (~70 % by mass) and in the **sun**; on Earth third most abundant.',
        hindi: '**ब्रह्मांड** में सबसे प्रचुर तत्व (द्रव्यमान के अनुसार ~70%) और **सूर्य** में; पृथ्वी पर तीसरा सबसे प्रचुर। **लैब** (सामान्य): Zn + dil H₂SO₄ → ZnSO₄ + H₂↑। (शुद्ध Zn अक्रियाशील है; अशुद्ध Zn या Zn-Hg काम करता है।) HNO₃ से बचें जो H₂ को ऑक्सीकृत करता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'preparation',
      title: {
        english: 'Preparation of Dihydrogen',
        hindi: 'डाइहाइड्रोजन की तैयारी',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Lab** (commonest): Zn + dil H₂SO₄ → ZnSO₄ + H₂↑. (Pure Zn is unreactive; impure Zn or Zn–Hg works.) Avoid HNO₃ which oxidises H₂.',
        hindi: '**लैब** (सामान्य): Zn + dil H₂SO₄ → ZnSO₄ + H₂↑। (शुद्ध Zn अक्रियाशील है; अशुद्ध Zn या Zn-Hg काम करता है।) HNO₃ से बचें जो H₂ को ऑक्सीकृत करता है।',
            },
            {
              english: 'From NaOH: $Zn + 2NaOH \\to Na_2ZnO_2 + H_2$.',
        hindi: 'NaOH से: $Zn + 2NaOH \\to Na_2ZnO_2 + H_2$.',
            },
            {
              english: '**Industrial — water-gas / steam-reforming**: $CH_4 + H_2O \\xrightarrow{1270\\,K, Ni} CO + 3H_2$. Followed by water-gas shift: $CO + H_2O \\xrightarrow{673\\,K, Fe/Cr_2O_3} CO_2 + H_2$.',
        hindi: '**औद्योगिक - जल-गैस/भाप-सुधार**: $CH_4 + H_2O \\xrightarrow{1270\\,K, Ni} CO + 3H_2$। जल-गैस शिफ्ट के बाद: $CO + H_2O \\xrightarrow{673\\,K, Fe/Cr_2O_3} CO_2 + H_2$।',
            },
            {
              english: '**Bosch process**: water gas + steam over Fe₂O₃/Cr₂O₃; CO₂ removed under pressure with K₂CO₃ giving KHCO₃.',
        hindi: '**बॉश प्रक्रिया**: जल गैस + Fe₂O₃/Cr₂O₃ पर भाप; K₂CO₃ को KHCO₃ देकर दबाव में CO₂ को हटा दिया गया।',
            },
            {
              english: '**Coal gasification**: $C + H_2O \\to CO + H_2$.',
        hindi: '**कोयला गैसीकरण**: $C + H_2O \\to CO + H_2$.',
            },
            {
              english: '**Electrolysis** of water (acidified or with Ba(OH)₂) → very pure H₂; energy-intensive.',
        hindi: '**पानी का इलेक्ट्रोलिसिस** (अम्लीकृत या Ba(OH)₂ के साथ) → बहुत शुद्ध H₂; ऊर्जा गहन.',
            },
          ],
        },
      ],
    },
    {
      id: 'properties',
      title: {
        english: 'Physical & Chemical Properties of H₂',
        hindi: 'H₂ के भौतिक एवं रासायनिक गुण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Colourless, odourless, tasteless, lightest gas. b.p. 20.4 K, m.p. 13.96 K. Sparingly soluble in water.',
        hindi: 'रंगहीन, गंधहीन, स्वादहीन, सबसे हल्की गैस। बी.पी. 20.4 के, एम.पी. 13.96 K. पानी में अल्प घुलनशील।',
            },
            {
              english: 'H–H bond enthalpy: **435.9 kJ/mol** — among the highest for single bonds; makes H₂ inert at room temperature.',
        hindi: 'एच-एच बांड एन्थैल्पी: **435.9 kJ/mol** - एकल बांड के लिए उच्चतम में से; कमरे के तापमान पर H₂ को निष्क्रिय बना देता है।',
            },
            {
              english: 'Combustion: $2H_2 + O_2 \\to 2H_2O,\\ \\Delta H = -286$ kJ/mol H₂O. Highly exothermic; basis of clean fuel.',
        hindi: 'दहन: $2H_2 + O_2 \\to 2H_2O,\\ \\Delta H = -286$ kJ/mol H₂O. अत्यधिक ऊष्माक्षेपी; स्वच्छ ईंधन का आधार.',
            },
            {
              english: 'With halogens: $H_2 + X_2 \\to 2HX$. Reactivity: F₂ > Cl₂ > Br₂ > I₂ (F₂ explosive in dark).',
        hindi: 'हैलोजन के साथ: $H_2 + X_2 \\to 2HX$। प्रतिक्रियाशीलता: F₂ > Cl₂ > Br₂ > I₂ (अंधेरे में F₂ विस्फोटक)।',
            },
            {
              english: 'With N₂ (Haber): $N_2 + 3H_2 \\xrightarrow{Fe, 700\\,K, 200\\,atm} 2NH_3$.',
        hindi: 'N₂ (हैबर) के साथ: $N_2 + 3H_2 \\xrightarrow{Fe, 700\\,K, 200\\,atm} 2NH_3$।',
            },
            {
              english: 'Reduces metal oxides: CuO + H₂ → Cu + H₂O.',
        hindi: 'धातु ऑक्साइड को कम करता है: CuO + H₂ → Cu + H₂O।',
            },
            {
              english: 'Acts as oxidising agent with active metals (forms saline hydrides) and as reducing agent with non-metals.',
        hindi: 'सक्रिय धातुओं के साथ ऑक्सीकरण एजेंट के रूप में कार्य करता है (खारा हाइड्राइड बनाता है) और गैर-धातुओं के साथ कम करने वाले एजेंट के रूप में कार्य करता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'hydrides',
      title: {
        english: 'Classification of Hydrides',
        hindi: 'हाइड्राइड का वर्गीकरण',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Type', 'Bonding', 'Examples', 'Notes'],
          rows: [
            [
              'Ionic / saline',
              'Mostly with s-block (except Be, Mg)',
              'NaH, CaH₂',
              'H as $H^-$; react vigorously with H₂O liberating H₂; powerful reducing agents (CaH₂ "hydrolith").',
            ],
            [
              'Covalent / molecular',
              'p-block elements',
              'CH₄, NH₃, H₂O, HF, B₂H₆',
              'Discrete molecules; often gaseous; further sub-classified as electron-precise (CH₄), electron-deficient (B₂H₆), electron-rich (NH₃, H₂O, HF).',
            ],
            [
              'Metallic / interstitial',
              'd- and f-block (except Gp 7, 8, 9 — "hydride gap")',
              'TiH₁.₇₃, LaH₂.₇₆, PdH₀.₆',
              'Often non-stoichiometric; hydrogen occupies interstitial sites; useful for H₂ storage.',
            ],
          ],
        },
      ],
    },
    {
      id: 'water',
      title: {
        english: 'Water — Structure, Hardness & Heavy Water',
        hindi: 'जल — संरचना, कठोरता और भारी जल',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'H₂O: bent ($\\angle$HOH ≈ 104.5°), $sp^3$ hybrid O with 2 lone pairs; extensive H-bonding ⇒ high b.p., high specific heat, high $\\Delta_{vap} H$.',
        hindi: 'H₂O: मुड़ा हुआ ($\\angle$HOH ≈ 104.5°), $sp^3$ हाइब्रिड O 2 अकेले जोड़े के साथ; व्यापक एच-बॉन्डिंग ⇒ उच्च बी.पी., उच्च विशिष्ट ऊष्मा, उच्च $\\Delta_{vap} H$।',
            },
            {
              english: 'Density max at 4 °C; ice less dense than water (open hexagonal H-bonded lattice) ⇒ ice floats.',
        hindi: 'घनत्व अधिकतम 4 डिग्री सेल्सियस पर; बर्फ पानी से कम घनी होती है (खुली हेक्सागोनल एच-बंधित Common Trapी) ⇒ बर्फ तैरती है।',
            },
            {
              english: 'Amphoteric: behaves as both acid and base. Self-ionisation $K_w = 10^{-14}$ at 298 K.',
        hindi: 'एम्फोटेरिक: अम्ल और क्षार दोनों के रूप में व्यवहार करता है। 298 K पर स्व-आयनीकरण $K_w = 10^{-14}$।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Hardness of water',
        hindi: 'पानी की कठोरता',
          },
          body: {
            english: `**Temporary** (carbonate) hardness — due to dissolved Ca(HCO₃)₂ / Mg(HCO₃)₂; removed by **boiling** or **Clark's process** (Ca(OH)₂).
**Permanent** (non-carbonate) hardness — due to chlorides / sulphates of Ca²⁺, Mg²⁺; removed by **washing soda** Na₂CO₃, **ion-exchange resins**, **Calgon** Na₆P₆O₁₈, or **zeolite (permutit)** process.`,
            hindi: '**अस्थायी** (कार्बोनेट) कठोरता - घुले हुए Ca(HCO₃)₂ / Mg(HCO₃)₂ के कारण; **उबालने** या **क्लार्क की प्रक्रिया** (Ca(OH)₂) द्वारा हटा दिया जाता है। **स्थायी** (गैर-कार्बोनेट) कठोरता - Ca²⁺, Mg²⁺ के क्लोराइड/सल्फेट के कारण; **वाशिंग सोडा** Na₂CO₃, **आयन-एक्सचेंज रेजिन**, **Calgon** Na₆P₆O₁₈, या **zeolite (permutit)** प्रक्रिया द्वारा हटाया गया।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Heavy water** D₂O: prepared by exhaustive electrolysis (rate of $D_2O$ electrolysis is much slower than $H_2O$). b.p. 101.4 °C, density 1.106 g/cm³ at 20 °C. Used as moderator in nuclear reactors.',
        hindi: '**भारी पानी** D₂O: संपूर्ण इलेक्ट्रोलिसिस द्वारा तैयार ($D_2O$ इलेक्ट्रोलिसिस की दर $H_2O$ की तुलना में बहुत धीमी है)। बी.पी. 101.4 डिग्री सेल्सियस, घनत्व 1.106 g/cm³ 20 डिग्री सेल्सियस पर। परमाणु रिएक्टरों में मॉडरेटर के रूप में उपयोग किया जाता है।',
            },
            {
              english: '**Hydrogen peroxide** H₂O₂: open-book structure (dihedral ≈ 111° gas, 90.2° solid). Both oxidising and reducing agent. Sold as 30 % "100-volume" solution. Stored in plastic, dark; decomposed by trace metals & alkali.',
        hindi: '**हाइड्रोजन पेरोक्साइड** H₂O₂: ओपन-बुक संरचना (डायहेड्रल ≈ 111° गैस, 90.2° ठोस)। ऑक्सीकरण और कम करने वाले एजेंट दोनों। 30% "100-वॉल्यूम" समाधान के रूप में बेचा जाता है। प्लास्टिक में संग्रहित, अंधेरा; ट्रेस धातुओं और क्षार द्वारा विघटित।',
            },
          ],
        },
      ],
    },
    {
      id: 'fuel',
      title: {
        english: 'Hydrogen Economy',
        hindi: 'हाइड्रोजन अर्थव्यवस्था',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'High calorific value (~143 kJ/g vs ~50 for petrol). Combustion product is only water — pollution-free.',
        hindi: 'उच्च कैलोरी मान (पेट्रोल के लिए ~143 kJ/g बनाम ~50)। दहन उत्पाद केवल पानी है - प्रदूषण मुक्त।',
            },
            {
              english: 'Storage problems: very low density of liquid H₂; modern solutions: metal hydrides, carbon nanotubes, compressed gas at 700 bar.',
        hindi: 'भंडारण की समस्याएँ: तरल H₂ का बहुत कम घनत्व; आधुनिक समाधान: धातु हाइड्राइड, कार्बन नैनोट्यूब, 700 बार पर संपीड़ित गैस।',
            },
            {
              english: '**Fuel cell**: H₂ + O₂ → H₂O with direct conversion of chemical to electrical energy; used in spacecraft (Apollo, Space Shuttle).',
        hindi: '**ईंधन सेल**: H₂ + O₂ → H₂O रसायन के सीधे विद्युत ऊर्जा में रूपांतरण के साथ; अंतरिक्ष यान (अपोलो, स्पेस शटल) में उपयोग किया जाता है।',
            },
            {
              english: 'Industrial uses: synthesis of NH₃ (Haber), CH₃OH, hydrogenation of vegetable oils (Ni catalyst → vanaspati), reducing agent in metallurgy, rocket fuel (liquid H₂ + liquid O₂).',
        hindi: 'औद्योगिक उपयोग: NH₃ (हैबर), CH₃OH का संश्लेषण, वनस्पति तेलों का हाइड्रोजनीकरण (Ni उत्प्रेरक → वनस्पति), धातु विज्ञान में कम करने वाला एजेंट, रॉकेट ईंधन (तरल H₂ + तरल O₂)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'CH_4 + H_2O \\xrightarrow{Ni} CO + 3H_2',
      desc: {
        english: 'Steam reforming',
        hindi: 'भाप सुधार',
      },
    },
    {
      latex: 'CO + H_2O \\xrightarrow{Fe/Cr_2O_3} CO_2 + H_2',
      desc: {
        english: 'Water-gas shift',
        hindi: 'जल-गैस परिवर्तन',
      },
    },
    {
      latex: 'BE(H{-}H) = 435.9\\ \\text{kJ/mol}',
      desc: {
        english: 'H–H bond strength',
        hindi: 'एच-एच बंधन शक्ति',
      },
    },
    {
      latex: 'Ca(HCO_3)_2 + Ca(OH)_2 \\to 2CaCO_3 + 2H_2O',
      desc: {
        english: 'Clark\'s process',
        hindi: 'क्लार्क की प्रक्रिया',
      },
    },
    {
      latex: 'Na_6P_6O_{18}',
      desc: {
        english: 'Calgon (sodium hexametaphosphate)',
        hindi: 'कैलगॉन (सोडियम हेक्सामेटाफॉस्फेट)',
      },
    },
    {
      latex: '\\Delta_c H(H_2) = -286\\ \\text{kJ/mol}',
      desc: {
        english: 'Combustion enthalpy',
        hindi: 'दहन एन्थैल्पी',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Hydrides',
      problem: {
        english: 'Which is an electron-deficient hydride?',
        hindi: 'इलेक्ट्रॉन की कमी वाला हाइड्राइड कौन सा है?',
      },
      answer: {
        english: 'B₂H₆',
        hindi: 'B₂H₆',
      },
      solution: {
        english: 'Diborane has 3-centre 2-electron bonds.',
        hindi: 'डिबोरेन में 3-केंद्र 2-इलेक्ट्रॉन बांड हैं।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Hardness',
      problem: {
        english: 'Calgon used for water softening is:',
        hindi: 'पानी को नरम करने के लिए उपयोग किया जाने वाला कैलगन है:',
      },
      answer: {
        english: 'Na₆P₆O₁₈',
        hindi: 'Na₆P₆O₁₈',
      },
      solution: {
        english: 'Sodium hexametaphosphate.',
        hindi: 'सोडियम हेक्सामेटाफॉस्फेट।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Heavy water',
      problem: {
        english: 'Heavy water is principally used in nuclear reactors as:',
        hindi: 'भारी पानी का उपयोग मुख्य रूप से परमाणु रिएक्टरों में किया जाता है:',
      },
      answer: {
        english: 'Moderator',
        hindi: 'मॉडरेटर',
      },
      solution: {
        english: 'D₂O slows fast neutrons without absorbing them.',
        hindi: 'D₂O तेज न्यूट्रॉनों को अवशोषित किए बिना उन्हें धीमा कर देता है।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'H₂O₂',
      problem: {
        english: 'In H₂O₂ molecule, dihedral angle in gas phase is:',
        hindi: 'H₂O₂ अणु में, गैस चरण में डायहेड्रल कोण है:',
      },
      answer: {
        english: '≈ 111°',
        hindi: '≈ 111°',
      },
      solution: {
        english: 'Open-book / skew structure (90.2° in solid).',
        hindi: 'ओपन-बुक / तिरछी संरचना (ठोस में 90.2°)।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Isotopes',
      problem: {
        english: 'Tritium is a:',
        hindi: 'ट्रिटियम एक है:',
      },
      answer: {
        english: 'β⁻ emitter, $t_{1/2} \\approx 12.3$ y',
        hindi: 'β⁻ उत्सर्जक, $t_{1/2} \\approx 12.3$ y',
      },
      solution: {
        english: '$_1^3 H \\to {}_2^3 He + \\beta^-$.',
        hindi: '$_1^3 H \\to {}_2^3 He + \\beta^-$। **अस्थायी** (कार्बोनेट) कठोरता - घुले हुए Ca(HCO₃)₂ / Mg(HCO₃)₂ के कारण; **उबालने** या **क्लार्क की प्रक्रिया** (Ca(OH)₂) द्वारा हटा दिया जाता है। **स्थायी** (गैर-कार्बोनेट) कठोरता - Ca²⁺, Mg²⁺ के क्लोराइड/सल्फेट के कारण; **वाशिंग सोडा** Na₂CO₃, **आयन-एक्सचेंज रेजिन**, **Calgon** Na₆P₆O₁₈, या **zeolite (permutit)** प्रक्रिया द्वारा हटाया गया। **खनिज**: धातु युक्त प्राकृतिक रूप से पाया जाने वाला रासायनिक पदार्थ। **अयस्क**: एक खनिज जिससे धातु **आर्थिक रूप से** निकाली जा सकती है।',
      },
    },
  ],
  next: {
    slug: 's-block',
    title: 's-Block Elements — Alkali & Alkaline-Earth Metals',
  },
};

export default chapter;
